import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  CSSProperties,
} from 'react';
import styled from 'styled-components';

import { useElementSize } from '@hooks/useElementSize';
import { DefaultText } from '@components/text';

const STAGE_RANGES = [
  { min: 0, max: 15 },
  { min: 16, max: 25 },
  { min: 26, max: 35 },
  { min: 36, max: 45 },
  { min: 46, max: 55 },
  { min: 56, max: 65 },
  { min: 66, max: 75 },
  { min: 76, max: 85 },
  { min: 86, max: 100 },
];

const generateRandomPercentages = (): number[] => {
  const percentages: number[] = [];

  for (let i = 0; i < STAGE_RANGES.length; i++) {
    const range = STAGE_RANGES[i];
    const randomPercentage =
      Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
    percentages.push(randomPercentage);
  }

  return percentages;
};

type Props = {
  isProcessing: boolean;
  onComplete?: () => void;
};

const LINES_ANIMATION_DURATION = 3000;
const PROGRESS_ANIMATION_DURATION = 500;
const DEFAULT_STEP_DURATION = 3000;
const FAST_COMPLETION_DURATION = 500;
const LINE_WIDTH = 24;

const Container = styled.div`
  width: 100%;
  margin: 8px 0;
  padding: 0 16px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const ProgressText = styled(DefaultText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xs};
  color: ${({ theme }) => theme.colors.white};
`;

const ProgressBarContainer = styled.div`
  position: relative;
  overflow: hidden;
  height: 16px;
  border-radius: 8px;
  align-self: stretch;
  background-color: ${({ theme }) => theme.colors.white};
  margin-top: 4px;
`;

const GradientBackground = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    270deg,
    ${({ theme }) => theme.colors.red[500]} 0%,
    ${({ theme }) => theme.colors.purple[500]} 50%,
    ${({ theme }) => theme.colors.blue[500]} 100%
  );
`;

const WhiteCover = styled.div.attrs<{ $width: number; $duration: number }>(
  ({ $width, $duration }) => ({
    style: {
      width: `${$width}px`,
      transition: `width ${$duration}ms ease-out`,
    },
  }),
)<{ $width: number; $duration: number }>`
  position: absolute;
  right: 0;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.white};
`;

const TiltedLinesContainer = styled.div.attrs<{ $translateX: number }>(
  ({ $translateX }) => ({
    style: {
      transform: `translateX(${$translateX}px)`,
    },
  }),
)<{ $translateX: number }>`
  display: flex;
  flex-direction: row;
  height: 48px;
  position: absolute;
  top: -16px;
  animation: slideLines ${LINES_ANIMATION_DURATION}ms linear infinite;

  @keyframes slideLines {
    0% {
      transform: translateX(var(--translate-x));
    }
    100% {
      transform: translateX(0px);
    }
  }
`;

const TiltedLine = styled.div`
  width: 12px;
  height: 48px;
  margin: 0 12px;
  transform: rotate(45deg);
  background-color: ${({ theme }) =>
    `${theme.colors.white}${theme.opacity[10]}`};
`;

const ProgressBar: FC<Props> = ({ isProcessing, onComplete }) => {
  const [stage, setStage] = useState<number>(0);
  const [processPercentages] = useState<number[]>(() =>
    generateRandomPercentages(),
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementSize(containerRef, {
    widthOnly: true,
  });
  const progress = useMemo(
    () => processPercentages[stage],
    [processPercentages, stage],
  );
  const lineWidth = useMemo(() => containerWidth || 400, [containerWidth]);
  const linesCount = useMemo(
    () => Math.ceil((lineWidth * 2) / LINE_WIDTH),
    [lineWidth],
  );
  const linesContainerWidth = useMemo(
    () => linesCount * LINE_WIDTH,
    [linesCount],
  );
  const initPosition = useMemo(
    () => linesContainerWidth / -2,
    [linesContainerWidth],
  );
  const coverWidth = useMemo(
    () => lineWidth * (1 - progress / 100),
    [lineWidth, progress],
  );

  const tiltedLines = useMemo(() => {
    return Array(linesCount)
      .fill(null)
      .map((_, index) => <TiltedLine key={index} />);
  }, [linesCount]);

  const updateProgress = useCallback(
    (newStage: number, duration: number = DEFAULT_STEP_DURATION) => {
      timeoutRef.current = setTimeout(() => {
        if (newStage === processPercentages.length && onComplete) {
          onComplete();
        } else {
          setStage(newStage);
        }
      }, duration);
    },
    [onComplete, processPercentages.length],
  );

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (isProcessing) {
      if (stage === processPercentages.length - 2) {
        return;
      }
      updateProgress(stage + 1);
    } else {
      if (stage !== processPercentages.length) {
        updateProgress(stage + 1, FAST_COMPLETION_DURATION);
      }
    }
  }, [stage, isProcessing, updateProgress, processPercentages.length]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Container ref={containerRef}>
      <TextContainer>
        <ProgressText>{progress}%</ProgressText>
      </TextContainer>

      <ProgressBarContainer>
        <GradientBackground />

        <WhiteCover
          $width={coverWidth}
          $duration={PROGRESS_ANIMATION_DURATION}
        />

        <TiltedLinesContainer
          $translateX={initPosition}
          style={{ '--translate-x': `${initPosition}px` } as CSSProperties}
        >
          {tiltedLines}
        </TiltedLinesContainer>
      </ProgressBarContainer>
    </Container>
  );
};

export default memo(ProgressBar);
